import { useEffect, useState } from "react";
import { AccountsPagination } from "@/bizminer/App/Accounts/_components/AccountsPagination";
import { FilterDropdown } from "@/bizminer/App/Accounts/_components/FilterDropdown";
import { SearchInput } from "@/components/SearchInput";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout";
import type { PagyMetadata } from "@/types";
import type { BizminerRelatedInitiative } from "@/types/serializers";
import { RelatedInitiativeSheet } from "./_components/RelatedInitiativeSheet";
import { RelatedInitiativesTable } from "./_components/RelatedInitiativesTable";

const FILTERS = [
  { label: "Companies", paramName: "company", key: "companies" },
  { label: "Pillars", paramName: "pillar", key: "pillars" },
  { label: "Industries", paramName: "industry", key: "industries" },
  { label: "Impact Categories", paramName: "impact_type", key: "impact_types" },
  { label: "Beneficiaries", paramName: "beneficiary", key: "beneficiaries" },
  { label: "Evidence", paramName: "evidence_type", key: "evidence_types" },
] as const;

interface FilterOption {
  value: string;
  label: string;
}

interface Props {
  related_initiatives: BizminerRelatedInitiative[];
  filter_options: Record<(typeof FILTERS)[number]["key"], FilterOption[]>;
  pagy: PagyMetadata;
  search: string;
}

export default function Index({
  related_initiatives,
  filter_options,
  pagy,
  search,
}: Props) {
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [related_initiatives]);

  const selectedRi = selectedIndex >= 0 ? related_initiatives[selectedIndex] : null;
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex >= 0 && selectedIndex < related_initiatives.length - 1;

  return (
    <BizminerLayout>
      <div className="space-y-8">
        <h1 className="text-2xl font-bold">Related Initiatives</h1>

        <div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <SearchInput
              initialValue={search}
              placeholder="Search related initiatives..."
            />
            {FILTERS.map(({ label, paramName, key }) => (
              <FilterDropdown
                key={key}
                label={label}
                paramName={paramName}
                options={filter_options[key]}
              />
            ))}
          </div>

          <RelatedInitiativesTable
            relatedInitiatives={related_initiatives}
            onSelect={(index) => setSelectedIndex(index)}
          />

          {related_initiatives.length > 0 && <AccountsPagination pagy={pagy} />}
        </div>
      </div>

      <RelatedInitiativeSheet
        ri={selectedRi}
        open={selectedRi !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedIndex(-1);
        }}
        onPrev={() => hasPrev && setSelectedIndex(selectedIndex - 1)}
        onNext={() => hasNext && setSelectedIndex(selectedIndex + 1)}
        hasPrev={hasPrev}
        hasNext={hasNext}
      />
    </BizminerLayout>
  );
}
